/**
 * Model catalog for the picker.
 *
 * Live source: the terminal pod's shim exposes `GET /api/models`, which
 * shells out to `openclaw models list --json` and maps the result to
 * `ModelInfo`. `fetchAvailableModels` hits that endpoint; on success the
 * picker reflects whatever the gateway is configured with, no UI redeploy
 * required. On failure (offline pod, old shim, unknown host) we fall back
 * to the static table below — region-pinned per cluster so a user on a
 * jp-region pod doesn't see eu-only ids that the gateway would reject.
 *
 * Selection still goes over `/model <id>` on the existing WebSocket. The
 * shim returns `id` as the unprefixed CLI `name` (e.g. `anthropic.claude-
 * opus-4-7`); on the fallback path `id` is the regioned Bedrock id from
 * the static table. The gateway accepts the static-table form today.
 */

import type { ModelInfo } from './types';

const MODELS_BY_HOST_SUFFIX: Readonly<Record<string, readonly ModelInfo[]>> = {
  // infra/helm/terminal/environments/terminal-paradigm-prod.yaml
  'terminal.paradigm.tech': [
    { id: 'eu.anthropic.claude-sonnet-4-6', label: 'Sonnet 4.6' },
    {
      id: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0',
      label: 'Haiku 4.5',
    },
    { id: 'eu.anthropic.claude-opus-4-7', label: 'Opus 4.7', legacy: true },
  ],
  // infra/helm/terminal/environments/terminal-prod.yaml (DIME cluster, JP region)
  'terminal.dime.gg': [
    { id: 'jp.anthropic.claude-opus-4-8', label: 'Opus 4.8' },
    { id: 'jp.anthropic.claude-opus-4-7', label: 'Opus 4.7', legacy: true },
    { id: 'jp.anthropic.claude-sonnet-4-6', label: 'Sonnet 4.6' },
    {
      id: 'jp.anthropic.claude-haiku-4-5-20251001-v1:0',
      label: 'Haiku 4.5',
    },
    // DeepSeek is the only non-Claude entry the gateway exposes today,
    // and OpenClaw doesn't curate a friendly name for it — the CLI
    // returns the raw `amazon-bedrock/<id>` for both `key` and `name`,
    // so the id format below is also what we send to `/model <id>` to
    // switch (verified manually against terminal-prod).
    { id: 'amazon-bedrock/deepseek.v3.2', label: 'DeepSeek 3.2' },
  ],
};

export function getStaticModels(agentUrl: string | null): readonly ModelInfo[] {
  if (agentUrl == null || agentUrl.length === 0) return [];
  let host: string;
  try {
    host = new URL(agentUrl).hostname;
  } catch {
    return [];
  }
  for (const [suffix, models] of Object.entries(MODELS_BY_HOST_SUFFIX)) {
    // Anchored suffix match — `evilterminal.paradigm.tech` must not match
    // `terminal.paradigm.tech`; only exact or proper-subdomain matches.
    if (host === suffix || host.endsWith(`.${suffix}`)) return models;
  }
  // Unknown host (custom Lightsail, dev forks) — return empty rather
  // than guess a list. Each cluster's `BEDROCK_MODEL_IDS` is
  // region-pinned (jp.* vs eu.*); guessing the wrong region would have
  // the picker offer ids the gateway will reject. The picker hides
  // itself on empty.
  return [];
}

// Flat lookup of every known Bedrock id → its friendly label, across
// all clusters. Used by `rewriteModelMentions` so the gateway's verbose
// "Model set to amazon-bedrock/<id> for this session." chat replies can
// be displayed as "Model set to Sonnet 4.6 for this session." in the UI.
const ALL_LABELS_BY_ID: ReadonlyMap<string, string> = new Map(
  Object.values(MODELS_BY_HOST_SUFFIX).flatMap((models) =>
    models.map((m) => [m.id, m.label] as const),
  ),
);

// Same map keyed by the unprefixed id (region prefix stripped). The
// shim's live `/api/models` returns the CLI's `name` field, which is
// region-less (`anthropic.claude-opus-4-7`), while the static table
// stores region-pinned ids (`jp.anthropic.…`, `eu.anthropic.…`). Without
// this second index, friendly-label substitution in `coerceModels` would
// miss every Claude row and only fire for DeepSeek (whose id has no
// region prefix in either source). Collisions across regions are safe
// because both `jp.*` and `eu.*` entries for the same model carry the
// same label.
const STRIPPED_LABELS_BY_ID: ReadonlyMap<string, string> = new Map(
  Object.values(MODELS_BY_HOST_SUFFIX).flatMap((models) =>
    models.map((m) => [m.id.replace(/^[a-z]{2}\./u, ''), m.label] as const),
  ),
);

export function getModelLabel(modelId: string): string {
  return ALL_LABELS_BY_ID.get(modelId) ?? modelId;
}

/**
 * Derive the HTTP(S) base URL of the terminal pod from its WebSocket URL.
 * `wss://x.terminal.paradigm.tech/agent` → `https://x.terminal.paradigm.tech`.
 * Returns `null` for inputs we can't parse so callers degrade to the static
 * table rather than firing a request at a guessed origin.
 */
function deriveHttpBase(agentUrl: string | null): string | null {
  if (agentUrl == null || agentUrl.length === 0) return null;
  try {
    const u = new URL(agentUrl);
    const protocol =
      u.protocol === 'wss:'
        ? 'https:'
        : u.protocol === 'ws:'
        ? 'http:'
        : u.protocol;
    if (protocol !== 'https:' && protocol !== 'http:') return null;
    return `${protocol}//${u.host}`;
  } catch {
    return null;
  }
}

function coerceModels(json: unknown): readonly ModelInfo[] {
  if (json == null || typeof json !== 'object') return [];
  const { models } = json as { readonly models?: unknown };
  if (!Array.isArray(models)) return [];
  const out: ModelInfo[] = [];
  for (const entry of models as readonly unknown[]) {
    if (entry == null || typeof entry !== 'object') continue;
    const rec = entry as {
      readonly id?: unknown;
      readonly label?: unknown;
      readonly legacy?: unknown;
    };
    if (typeof rec.id !== 'string' || typeof rec.label !== 'string') continue;
    // OpenClaw curates friendly names for first-party models (e.g. Claude's
    // `key` is "Opus 4.7") but falls back to the raw provider-prefixed id
    // for third-party models, so we substitute a friendly name from the
    // static table when available. The CLI returns region-less ids while
    // the static table keys with `jp.` / `eu.` prefixes — try both maps
    // so the substitution fires for Claude rows too, not just DeepSeek.
    const friendly =
      ALL_LABELS_BY_ID.get(rec.id) ?? STRIPPED_LABELS_BY_ID.get(rec.id);
    out.push({
      id: rec.id,
      label: friendly ?? rec.label,
      ...(rec.legacy === true ? { legacy: true } : {}),
    });
  }
  return out;
}

/**
 * Fetch the live model catalog from the terminal pod's shim. Returns an
 * empty array on any failure (network, non-200, bad payload) so callers can
 * fall back to `getStaticModels` without an extra try/catch. Uses
 * `credentials: 'include'` because the shim auth-gates `/api/models` via
 * the same cookie-based forward_auth as the rest of the upstream.
 */
export async function fetchAvailableModels(
  agentUrl: string | null,
  options?: { readonly signal?: AbortSignal },
): Promise<readonly ModelInfo[]> {
  const base = deriveHttpBase(agentUrl);
  if (base == null) return [];
  try {
    const res = await fetch(`${base}/api/models`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: options?.signal,
    });
    if (!res.ok) return [];
    const json = (await res.json()) as unknown;
    return coerceModels(json);
  } catch {
    return [];
  }
}

/**
 * True if `text` looks like the gateway's reply to `/model <id>` — used
 * to suppress the duplicate when the picker already added an optimistic
 * acknowledgment client-side.
 *
 * TODO(openclaw): brittle — depends on gateway wording staying as
 * `Model set to <provider>/<id> for this session.` / `Model reset to
 * default (...)`. Replace with a structured frame when the gateway
 * exposes one.
 */
export function isModelSwitchEcho(text: string): boolean {
  return (
    text.startsWith('Model set to amazon-bedrock/') ||
    text.startsWith('Model reset to default')
  );
}

/**
 * Cosmetic rewrites applied to assistant text at render time:
 *
 *   1. `amazon-bedrock/<bedrock-id>` → friendly label (e.g. `Sonnet 4.6`).
 *      Unknown ids pass through unchanged.
 *   2. OpenClaw's `/model` help output is one long line with `Label:
 *      value` sections — break each onto its own paragraph and bold the
 *      label, and wrap `/model[s]` slash commands in backticks. Gated on
 *      the input looking like a model-related message so normal prose
 *      isn't reformatted.
 *
 * Chat state stays untouched — this only affects the rendered string.
 */
export function rewriteModelMentions(text: string): string {
  const isModelText =
    text.includes('amazon-bedrock/') || /^Current:\s/u.test(text);
  let out = text.replace(
    /amazon-bedrock\/([\w.:-]+)/gu,
    (match, id: string) => ALL_LABELS_BY_ID.get(id) ?? match,
  );
  if (isModelText) {
    out = out.replace(/^(Current|Switch|Runtime|Browse|More):\s/u, '**$1:** ');
    out = out.replace(
      /\s(Current|Switch|Runtime|Browse|More):\s/gu,
      '\n\n**$1:** ',
    );
    out = out.replace(
      /(\/models?(?:\s+<[^>]+>)*(?:\s+--\w+(?:\s+<[^>]+>)?)?(?:\s+status)?)/gu,
      '`$1`',
    );
  }
  return out;
}
